import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
    catchError,
    exhaustMap,
    map,
    mergeMap,
    withLatestFrom
} from 'rxjs/operators';
import {
    DraftService,
    MotorsEditListingRequest,
    ListingRequestType,
    PhotoService,
    MotorsRelistListingRequest
} from 'libs/datalayer/src';
import * as DraftActions from './motors.draft.actions';
import * as DraftFeature from './motors.draft.reducer';
import * as DraftSelectors from './motors.draft.selectors';
import { MotorsDraftLoadResult } from '../models/motors-draft-load-result';

@Injectable()
export class MotorsDraftEffects {
    loadDraft$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.getDraft),
            withLatestFrom(this.store.select(DraftSelectors.getStateMotors)),
            mergeMap(([action, state]) => {
                if (state && state.categoryId > 0 && state.draftLoaded) {
                    return [DraftActions.noOpAction()];
                }

                const draft$ = action.listingId
                    ? this.draftService.getListingDraft<MotorsDraftLoadResult>(action.listingId, action.categoryId)
                    : this.draftService.getIncompleteDraft<MotorsDraftLoadResult>(action.categoryId);

                return draft$.pipe(
                    map((data: MotorsDraftLoadResult) => {
                        return DraftActions.getDraftSuccess({
                            draft: data,
                            isFromLocalStorage: data.draftLoadedFromLocalStorage || false
                        });
                    }),
                    catchError((error: unknown) =>
                        of(DraftActions.getDraftFailure({ error }))
                    )
                );
            })
        )
    );

    saveDraft$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.saveDraft),
            exhaustMap((action) =>
                this.draftService.saveDraft(action.draft).pipe(
                    map((response) =>
                        DraftActions.saveDraftSuccess({
                            result: response,
                            lastSavedStepId: JSON.parse(action.draft.draftData)
                                .lastSavedStepId
                        })
                    ),
                    catchError((error: unknown) =>
                        of(DraftActions.saveDraftFailure({ error }))
                    )
                )
            )
        )
    );

    makeListingAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.makeListingAction),
            exhaustMap((action) => {
                let apiRequest$;

                switch (action.listingRequest.type) {
                    case ListingRequestType.Create: {
                        apiRequest$ = this.draftService.createListing(
                            action.listingRequest.categoryId
                        );
                        break;
                    }

                    case ListingRequestType.Edit: {
                        const editRequest =
                            action.listingRequest as MotorsEditListingRequest;
                        apiRequest$ = this.draftService.editListing(
                            editRequest.listingId,
                            editRequest.categoryId,
                            editRequest.draft,
                            editRequest.listingEndDate);
                        break;
                    }

                    case ListingRequestType.Relist: {
                        const relistRequest =
                            action.listingRequest as MotorsRelistListingRequest;
                        apiRequest$ = this.draftService.relistListing(
                            relistRequest.listingId,
                            relistRequest.categoryId,
                            relistRequest.draft
                        );
                        break;
                    }
                }

                return apiRequest$.pipe(
                    map((response) =>
                        DraftActions.makeListingActionSuccess({
                            listingRequest: response,
                            listingRequestType: action.listingRequest.type
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DraftActions.makeListingActionFailure({
                                error,
                                listingRequestType: action.listingRequest.type
                            })
                        )
                    )
                );
            })
        )
    );


    validateDraftListing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.validateListing),
            exhaustMap(() => {
                return this.draftService.validateDraftListing().pipe(
                    map((response) =>
                        DraftActions.validateListingSuccess({
                            validListing: response
                        })
                    ),
                    catchError((error: unknown) =>
                        of(DraftActions.validateListingFailure({ error }))
                    )
                );
            })
        )
    );

    addPhotoToDraftData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.addPhotoToDraftData),
            map((action) =>
                DraftActions.queuePhotoUpload({
                    photoId: action.photo.photoId,
                    selectedPhotoData: action.selectedPhotoData,
                    parentProperty: action.parentProperty
                })
            )
        )
    );

    uploadPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.uploadPhoto),
            mergeMap((action) =>
                this.photoService.uploadPhoto(action.selectedPhotoData).pipe(
                    map((response) =>
                        DraftActions.uploadPhotoSuccess({
                            photoId: action.photoId,
                            photoUrls: response,
                            parentProperty: action.parentProperty
                        })
                    ),
                    catchError((error: unknown) => {
                        return of(
                            DraftActions.uploadPhotoFailure({
                                photoId: action.photoId,
                                error: error,
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    queuePhotoUpload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.queuePhotoUpload),
            mergeMap((action) =>
                this.photoService.queuePhotoForUpload(action.selectedPhotoData).pipe(
                    map((response) =>
                        DraftActions.uploadPhotoSuccess({
                            photoId: action.photoId,
                            photoUrls: response,
                            parentProperty: action.parentProperty
                        })
                    ),
                    catchError((error: unknown) => {
                        return of(
                            DraftActions.uploadPhotoFailure({
                                photoId: action.photoId,
                                error: error,
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    uploadPhotoSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.uploadPhotoSuccess),
            mergeMap((action) =>
                this.loadPhoto(action.photoUrls.plusSize).pipe(
                    map(() => DraftActions.noOpAction()),
                    catchError(() => {
                        return of(
                            DraftActions.updateInvalidPhoto({
                                photoId: action.photoUrls.photoId,
                                errorMessage:
                                    'Error occurred while loading the photo',
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    updateTransformPhotoDataToDraftData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.updateTransformPhotoDataToDraftData),
            map((action) =>
                DraftActions.transformPhoto({
                    photoId: action.editedPhotoData.photoId,
                    editedPhotoData: action.editedPhotoData,
                    parentProperty: action.parentProperty
                })
            )
        )
    );

    transformPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.transformPhoto),
            mergeMap((action) =>
                this.photoService
                    .transformPhoto({
                        ...action.editedPhotoData,
                        crop:
                            action.editedPhotoData.crop ||
                            action.editedPhotoData.maxBoundingBox // ahh.. TM API expects something to be provided always,
                        // but user has no intention of cropping the image
                        // so provide the full bounding box (o_O)
                    })
                    .pipe(
                        map((response) =>
                            DraftActions.transformPhotoSuccess({
                                photoId: action.photoId,
                                transformedPhoto: response,
                                crop: action.editedPhotoData.crop,
                                parentProperty: action.parentProperty
                            })
                        ),
                        catchError((error: unknown) => {
                            return of(
                                DraftActions.transformPhotoFailure({
                                    photoId: action.photoId,
                                    error: error,
                                    parentProperty: action.parentProperty
                                })
                            );
                        })
                    )
            )
        )
    );

    transformPhotoSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.transformPhotoSuccess),
            mergeMap((action) =>
                this.loadPhoto(action.transformedPhoto.plusSize).pipe(
                    map(() => DraftActions.noOpAction()),
                    catchError(() => {
                        return of(
                            DraftActions.updateInvalidPhoto({
                                photoId: action.photoId,
                                errorMessage:
                                    'Error occurred while loading the photo',
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    updateCurrentStep$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.updateCurrentStep),
            exhaustMap((action) =>
                this.draftService.updateCurrentStep(action.currentStep).pipe(
                    map((response) =>
                        DraftActions.updateCurrentStepSuccess({
                            result: response,
                            currentStepId: action.currentStep.currentStepId
                        })
                    ),
                    catchError((error: unknown) =>
                        of(DraftActions.updateCurrentStepFailure({ error }))
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store<DraftFeature.MotorsDraftPartialState>,
        private draftService: DraftService,
        private photoService: PhotoService
    ) { }

    private loadPhoto(url: string) {
        return new Observable<any>((o) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                o.complete();
            };
            img.onerror = (err) => {
                o.error(err);
            };
        });
    }
}
